import React from "react";
import { Box, Table, Thead, Tbody, Tr, Th, Td, Text } from "@chakra-ui/react";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";

export type MonthlySnapshotRow = {
    key: string;
    label: string;
    count: number;
    revenue?: number;
    extra?: number;
};

type MonthlySnapshotTableProps = {
    title: string;
    subtitle?: string;
    rows: MonthlySnapshotRow[];
    countLabel?: string;
    revenueLabel?: string;
    extraLabel?: string;
    showRevenue?: boolean;
    showExtra?: boolean;
};

export const MonthlySnapshotTable: React.FC<MonthlySnapshotTableProps> = ({
    title,
    subtitle,
    rows,
    countLabel = "Count",
    revenueLabel = "Revenue",
    extraLabel = "Extra",
    showRevenue = true,
    showExtra = false,
}) => {
    const { theme } = useCustomTheme();
    const isDark = theme.name === "dark";
    const cardsBorderRadii = "8px";

    return (
        <Box
            borderRadius={cardsBorderRadii}
            bg={theme.surface}
            boxShadow={theme.shadowSm}
            border={`1px solid ${theme.border}`}
            p={{ base: 3, md: 4 }}
            mb={3}
            position="relative"
            overflow="hidden"
            _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                bg: theme.gradients?.secondaryButton || theme.brand?.indigo || "#6f7cff",
            }}
        >
            <Box mb={3}>
                <Text color={theme.text} fontWeight="800">
                    {title}
                </Text>
                {subtitle ? (
                    <Text color={theme.textMuted} fontSize="sm">
                        {subtitle}
                    </Text>
                ) : null}
            </Box>

            {rows.length ? (
                <Box overflowX="auto">
                    <Table size="sm" minW="620px">
                        <Thead>
                            <Tr bg={isDark ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.6)"}>
                                <Th color={theme.textMuted}>Month</Th>
                                <Th color={theme.textMuted}>{countLabel}</Th>
                                {showRevenue && <Th color={theme.textMuted}>{revenueLabel}</Th>}
                                {showExtra && <Th color={theme.textMuted}>{extraLabel}</Th>}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {rows.map((m) => (
                                <Tr key={m.key}>
                                    <Td color={theme.text} fontWeight="600">{m.label}</Td>
                                    <Td color={theme.text}>{m.count}</Td>
                                    {showRevenue && (
                                        <Td color={theme.text}>
                                            MWK {Math.round(Number(m.revenue || 0)).toLocaleString()}
                                        </Td>
                                    )}
                                    {showExtra && <Td color={theme.text}>{m.extra ?? 0}</Td>}
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            ) : (
                <Text color={theme.textMuted} fontSize="sm">
                    No monthly data available for this selection.
                </Text>
            )}
        </Box>
    );
};